import Phaser from 'phaser';
import { TABLE_BG } from '../../config.js';
import { HighlightOverlay } from '../../components/HighlightOverlay.js';
import { landscapeLayout, portraitLayout } from '../../layout/tableLayout.js';
import { ATS_NUMBERS, atsStripLayout, buildPassLineZone, buildZonesFromSvg } from '../../layout/zones.js';
import { AtsBetZone } from '../../objects/AtsBetZone.js';
import { BetZone } from '../../objects/BetZone.js';
import { Dice } from '../../objects/Dice.js';
import { DiceThrow, type TweenAdapter } from '../../objects/DiceThrow.js';
import { createSimulator } from '../../objects/throwSimulator.js';
import { DEPTH, GESTURE, PHYSICS } from '../../objects/diceThrowConfig.js';
import * as feel from '../../feel.js';
import { Puck } from '../../objects/Puck.js';
import { track } from '../../analytics.js';

const HIGHLIGHT_DEPTH = 800;

/**
 * Mirror of CrapsTableScene minus the chip rack and bankroll plumbing.
 * Owns the felt SVGs, BetZones, dice, puck, and a HighlightOverlay used by
 * the tutorial driver. State changes (chips on zones, puck on/off) come
 * from the engine via tutorialBridge — same as the live table.
 */
export class CrapsTutorialScene extends Phaser.Scene {
  zones: BetZone[] = [];
  atsZones: AtsBetZone[] = [];
  dice1!: Dice;
  dice2!: Dice;
  puck!: Puck;
  highlight!: HighlightOverlay;
  private diceThrow!: DiceThrow;
  // Default OFF so dice can't be thrown before the tutorial bridge has set
  // a step's roll-gate state.
  private rollEnabled = false;
  private helpMode = false;
  private helpTint: Phaser.GameObjects.Rectangle | undefined;
  private helpBanner: Phaser.GameObjects.Text | undefined;

  constructor() {
    super('CrapsTutorialScene');
  }

  create() {
    this.zones = [];
    this.atsZones = [];
    track('tutorial_opened', { game: 'craps', topic: 'craps-standard' });
    this.cameras.main.setBackgroundColor(TABLE_BG);
    this.input.mouse?.disableContextMenu();

    const W = this.scale.width;
    const H = this.scale.height;
    const layout = H > W ? portraitLayout(W, H) : landscapeLayout(W, H);

    for (const panel of layout.panels) {
      const img = this.add.image(panel.dst.x, panel.dst.y, panel.svgKey);
      img.setOrigin(0, 0);
      img.setDisplaySize(panel.dst.w, panel.dst.h);
      img.setDepth(-10);
    }

    for (const panel of layout.panels) {
      const textKey = panel.svgKey === 'tableMain' ? 'tableMainSvgText' : 'tableHardwaySvgText';
      const svgText = this.cache.text.get(textKey) as string;
      const defs = buildZonesFromSvg(svgText, panel.dst);
      for (const def of defs) {
        const zone = new BetZone(this, def, {
          onClick: (z) => this.events.emit(this.helpMode ? 'zone:explain' : 'zone:click', z.def),
          onLongPress: (z) => {
            if (this.helpMode) return;
            this.events.emit('zone:longPress', z.def);
          },
        });
        this.zones.push(zone);
      }
      if (panel.svgKey === 'tableMain') {
        const def = buildPassLineZone(panel.dst, panel.viewBox);
        const zone = new BetZone(this, def, {
          onClick: (z) => this.events.emit(this.helpMode ? 'zone:explain' : 'zone:click', z.def),
          onLongPress: (z) => {
            if (this.helpMode) return;
            this.events.emit('zone:longPress', z.def);
          },
        });
        this.zones.push(zone);
      }
    }

    const diceSize = layout.dice.size;
    // Half-separation between dice centers — kept in sync with the live craps
    // scene. 0.75 leaves a visible gap between the dice so they read as two
    // cubes; the tutorial's highlight circle still wraps both at this width.
    const diceHalfSep = Math.round(diceSize * 0.75);
    // Dice live in CrapsTutorialDiceOverlayScene so they fly above the
    // highlight overlay, narration card, and help-mode tint.
    const overlay = this.scene.get('CrapsTutorialDiceOverlayScene') as
      import('./DiceOverlayScene.js').CrapsTutorialDiceOverlayScene;
    const created = overlay.setupDice({
      home: { x: layout.dice.x, y: layout.dice.y, halfSep: diceHalfSep },
      diceSize,
    });
    this.dice1 = created.dice1;
    this.dice2 = created.dice2;
    this.time.delayedCall(0, () => overlay.scene.bringToTop());

    const sim = createSimulator({
      bounds: { left: 0, right: W, top: 0, bottom: H },
      wallRestitution: PHYSICS.wallRestitution,
      pairRestitution: PHYSICS.pairRestitution,
      diceChipRestitution: PHYSICS.diceChipRestitution,
      settleVelThreshold: PHYSICS.settleVelThreshold,
      settleAngVelThreshold: PHYSICS.settleAngVelThreshold,
      settleFrames: PHYSICS.settleFrames,
      impactSpeedThreshold: PHYSICS.impactSpeedThreshold,
      lowSpeedThreshold: PHYSICS.lowSpeedThreshold,
      lowSpeedDampingPerSec: PHYSICS.lowSpeedDampingPerSec,
      lowSpeedAngDampingPerSec: PHYSICS.lowSpeedAngDampingPerSec,
      onImpact: (e) => {
        // Dice-only — chip-scatter bounces shouldn't buzz the phone.
        if (e.bodyId.startsWith('__dice_throw')) feel.wallImpactHaptic(e.speed);
        // Every wall touch shakes the camera; intensity scales with impact
        // speed. Bypasses feel.shake() so no color flash overlays —
        // a die hitting the rail shouldn't tint the felt red.
        if (!feel.shakeEnabled()) return;
        const [duration, intensity] = e.speed >= 1500 ? [320, 0.012]
                                    : e.speed >= 600  ? [180, 0.006]
                                    :                   [ 60, 0.002];
        this.cameras.main.shake(duration, intensity);
      },
    });

    const tween: TweenAdapter = {
      tween: ({ durationMs, onUpdate, onComplete }) => {
        const t = this.tweens.addCounter({
          from: 0, to: 1,
          duration: durationMs,
          ease: GESTURE.returnEase,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onUpdate: (tw) => onUpdate((tw as any).getValue() ?? 0),
          onComplete,
        });
        return { stop: () => t.stop() };
      },
    };

    this.diceThrow = new DiceThrow({
      dice1: this.dice1, dice2: this.dice2,
      dieHalfSize: diceSize / 2,
      home: { x: layout.dice.x, y: layout.dice.y, halfSep: diceHalfSep },
      bounds: { left: 0, right: W, top: 0, bottom: H },
      sim, tween,
      rollEnabled: () => this.rollEnabled,
      setRollEnabled: (v) => this.setRollEnabled(v),
      onRollRequest: () => this.events.emit('roll:request'),
    });

    const hit = this.add.rectangle(
      layout.dice.x, layout.dice.y,
      diceHalfSep * 2 + diceSize, diceSize,
      0x000000, 0,
    ).setInteractive({ useHandCursor: true });
    hit.on('pointerdown', (p: Phaser.Input.Pointer) => {
      this.diceThrow.onPointerDown(p.x, p.y, p.event.timeStamp);
    });
    this.input.on('pointermove', (p: Phaser.Input.Pointer) => {
      this.diceThrow.onPointerMove(p.x, p.y, p.event.timeStamp);
    });
    this.input.on('pointerup', (p: Phaser.Input.Pointer) => {
      this.diceThrow.onPointerUp(p.x, p.y, p.event.timeStamp);
    });
    this.input.on('pointerupoutside', (p: Phaser.Input.Pointer) => {
      this.diceThrow.onPointerUp(p.x, p.y, p.event.timeStamp);
    });

    const driveStep = (_t: number, deltaMs: number) => {
      const dt = deltaMs / 1000;
      sim.step(dt);
      this.diceThrow.update(dt);
    };
    this.events.on(Phaser.Scenes.Events.UPDATE, driveStep);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.events.off(Phaser.Scenes.Events.UPDATE, driveStep);
    });

    this.dice1.setDepth(DEPTH.diceThrow);
    this.dice2.setDepth(DEPTH.diceThrow);

    this.puck = new Puck(this, layout.puck.x, layout.puck.y, layout.puck.scale);

    // ATS strip — placement comes from the layout factory (portrait: right
    // of hardway, landscape: above hardway). Mirror CrapsTableScene's setup
    // so the tutorial can highlight SMALL / ALL / TALL when explaining the
    // bet, in either orientation.
    if (layout.atsStripRect) {
      const dpr = (this.game.registry.get('dpr') as number | undefined) ?? 1;
      const { cells, numberFontSize } = atsStripLayout(layout.atsStripRect, dpr);
      for (const cell of cells) {
        const numbers = ATS_NUMBERS[cell.id];
        const zone = new AtsBetZone({
          scene: this,
          rect: cell.rect,
          def: { id: cell.id, type: cell.id, label: cell.label, polygon: [], payoutHint: '' },
          numbers: numbers.numbers,
          twoRows: numbers.twoRows,
          numberFontSize,
          events: {
            onClick: (z) => this.events.emit(this.helpMode ? 'zone:explain' : 'zone:click', z.def),
            onLongPress: (z) => {
              if (this.helpMode) return;
              this.events.emit('zone:longPress', z.def);
            },
          },
        });
        this.atsZones.push(zone);
      }
    }

    this.highlight = new HighlightOverlay(this, HIGHLIGHT_DEPTH);
  }

  setRollEnabled(enabled: boolean) {
    this.rollEnabled = enabled;
    this.dice1?.setRollReady(enabled);
    this.dice2?.setRollReady(enabled);
  }

  // No-op: tutorial doesn't show the dice-disabled hint string. Defined so
  // tutorialBridge (forked from crapsBridge) can call it without branching.
  setRollHint(_msg: string | undefined) {
    /* no-op */
  }

  throwAndSettle(d1: number, d2: number, opts: { onSettle: () => void }): Promise<void> {
    return this.diceThrow.throwAndSettle(d1, d2, opts);
  }

  isHelpMode() {
    return this.helpMode;
  }

  setHelpMode(on: boolean) {
    this.helpMode = on;
    if (on) {
      if (!this.helpTint) {
        this.helpTint = this.add
          .rectangle(0, 0, this.scale.width, this.scale.height, 0x4a8fd4, 0.08)
          .setOrigin(0, 0)
          .setDepth(890);
      }
      if (!this.helpBanner) {
        const dpr = (this.game.registry.get('dpr') as number | undefined) ?? 1;
        // Anchored to the right of the ? pill so the text reads as a
        // direct caption to the toggle, not a centred banner that fights
        // the history strip below.
        this.helpBanner = this.add
          .text(
            Math.round(54 * dpr),
            Math.round(58 * dpr),
            'Tap any bet to learn about it · ? to exit',
            {
              fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
              fontSize: `${Math.round(13 * dpr)}px`,
              color: '#f4d35e',
              fontStyle: 'bold',
            },
          )
          .setOrigin(0, 0.5)
          .setDepth(891);
      }
    } else {
      this.helpTint?.destroy();
      this.helpTint = undefined;
      this.helpBanner?.destroy();
      this.helpBanner = undefined;
    }
  }
}
